import React, { useState, useEffect } from 'react';
import { Filter, X, Calendar } from 'lucide-react';
import FilterDropdown from './FilterDropdown';

const formatLocalDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AdvancedFilters = ({
  filters,
  setFilters,
  onClear,
  showPaymentStatus = false,
  showSalesperson = false,
  salespersons = [],
  statusOptions = []
}) => {
  const [customStart, setCustomStart] = useState(filters.startDate || '');
  const [customEnd, setCustomEnd] = useState(filters.endDate || '');
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);

  useEffect(() => {
    setCustomStart(filters.startDate || '');
    setCustomEnd(filters.endDate || '');
  }, [filters.startDate, filters.endDate]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const projectTypeOptions = [
    { value: 'all', label: 'All Project Types' },
    { value: 'true', label: 'Wood Work Projects' },
    { value: 'false', label: 'Retail Orders' }
  ];

  const paymentStatusOptions = [
    { value: 'all', label: 'All Payment Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'partial', label: 'Partial' },
    { value: 'pending', label: 'Pending' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: filters.startDate && filters.endDate ? `Custom (${filters.startDate} to ${filters.endDate})` : 'Custom Range' }
  ];

  const handleDateRangeChange = (value) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    switch (value) {
      case 'today':
        startDate = formatLocalDate(today);
        endDate = formatLocalDate(today);
        break;
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        startDate = formatLocalDate(yesterday);
        endDate = formatLocalDate(yesterday);
        break;
      }
      case 'this_week': {
        const day = today.getDay();
        const diff = (day === 0 ? -6 : 1) - day;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        startDate = formatLocalDate(monday);
        endDate = formatLocalDate(today);
        break;
      }
      case 'last_week': {
        const day = today.getDay();
        const diffToLastMonday = (day === 0 ? 7 : day) + 6;
        const lastMonday = new Date(today);
        lastMonday.setDate(today.getDate() - diffToLastMonday);
        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastMonday.getDate() + 6);
        startDate = formatLocalDate(lastMonday);
        endDate = formatLocalDate(lastSunday);
        break;
      }
      case 'last_7_days': {
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        startDate = formatLocalDate(last7);
        endDate = formatLocalDate(today);
        break;
      }
      case 'this_month': {
        const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = formatLocalDate(firstDayThisMonth);
        endDate = formatLocalDate(today);
        break;
      }
      case 'last_month': {
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = formatLocalDate(firstDayLastMonth);
        endDate = formatLocalDate(lastDayLastMonth);
        break;
      }
      case 'last_30_days': {
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 30);
        startDate = formatLocalDate(last30);
        endDate = formatLocalDate(today);
        break;
      }
      case 'last_6_months': {
        const last6 = new Date(today);
        last6.setMonth(today.getMonth() - 6);
        startDate = formatLocalDate(last6);
        endDate = formatLocalDate(today);
        break;
      }
      case 'this_year': {
        const firstDayThisYear = new Date(today.getFullYear(), 0, 1);
        startDate = formatLocalDate(firstDayThisYear);
        endDate = formatLocalDate(today);
        break;
      }
      case 'custom':
        setShowCustomDateModal(true);
        setFilters(prev => ({ ...prev, dateRange: value }));
        return;
      case 'all':
      default:
        startDate = '';
        endDate = '';
        break;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: value,
      startDate,
      endDate
    }));
  };

  const hasActiveFilters = Boolean(
    (filters.dateRange && filters.dateRange !== 'all') ||
    filters.startDate ||
    filters.endDate ||
    (filters.isWoodWork && filters.isWoodWork !== 'all') ||
    (filters.status && filters.status !== 'all') ||
    (filters.paymentStatus && filters.paymentStatus !== 'all') ||
    (filters.salesperson_id && filters.salesperson_id !== 'all')
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#C5A57D]/30 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2 text-[#0A5F38] font-semibold">
          <Filter className="w-5 h-5" />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="bg-[#E8F5E9] text-[#0A5F38] text-xs px-2 py-1 rounded-full font-medium">Active</span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="px-3 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors flex items-center space-x-1 text-sm cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
            <span>Clear All</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-[#0A5F38] uppercase tracking-wider">Date Range</label>
            {filters.dateRange === 'custom' && (
              <button
                onClick={() => setShowCustomDateModal(true)}
                className="text-xs text-[#C5A57D] hover:underline font-medium flex items-center gap-1 cursor-pointer"
              >
                <Calendar className="w-3 h-3" /> Edit Dates
              </button>
            )}
          </div>
          <FilterDropdown
            value={filters.dateRange || (filters.startDate ? 'custom' : 'all')}
            onChange={(e) => handleDateRangeChange(e.target.value)}
            options={dateRangeOptions}
            className="text-sm !py-2 h-[42px]"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-semibold text-[#0A5F38] uppercase tracking-wider">Project Type</label>
          <FilterDropdown
            value={filters.isWoodWork || 'all'}
            onChange={(e) => handleFilterChange('isWoodWork', e.target.value)}
            options={projectTypeOptions}
            className="text-sm !py-2 h-[42px]"
          />
        </div>

        {statusOptions && statusOptions.length > 0 && (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-[#0A5F38] uppercase tracking-wider">Document Status</label>
            <FilterDropdown
              value={filters.status || 'all'}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              options={statusOptions}
              className="text-sm !py-2 h-[42px]"
            />
          </div>
        )}

        {showPaymentStatus && (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-[#0A5F38] uppercase tracking-wider">Payment Status</label>
            <FilterDropdown
              value={filters.paymentStatus || 'all'}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              options={paymentStatusOptions}
              className="text-sm !py-2 h-[42px]"
            />
          </div>
        )}

        {showSalesperson && salespersons && salespersons.length > 0 && (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-[#0A5F38] uppercase tracking-wider">Salesperson</label>
            <FilterDropdown
              value={filters.salesperson_id || 'all'}
              onChange={(e) => handleFilterChange('salesperson_id', e.target.value)}
              options={[
                { value: 'all', label: 'All Salespersons' },
                ...salespersons.map(sp => ({
                  value: sp.id || sp._id || sp.salesperson_id,
                  label: sp.name || `Salesperson (${sp.salesperson_id})`
                }))
              ]}
              className="text-sm !py-2 h-[42px]"
            />
          </div>
        )}
      </div>

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 12, width: '100%', maxWidth: 400, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#0A5F38' }}>Select Custom Date Range</h3>
              <button onClick={() => setShowCustomDateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#94a3b8" /></button>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => {
                  if (!filters.startDate && !filters.endDate) {
                    setFilters(prev => ({ ...prev, dateRange: 'all' }));
                  }
                  setShowCustomDateModal(false);
                }}
                style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    dateRange: 'custom',
                    startDate: customStart,
                    endDate: customEnd
                  }));
                  setShowCustomDateModal(false);
                }}
                style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0A5F38', color: 'white', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;
